'use client';

/**
 * Message — adapted from AI Elements (https://elements.ai-sdk.dev/components/message)
 *
 * Simplified for current needs. Branching/toolbar can be re-added later.
 * AI Elements example uses plugins for cjk, code, math, mermaid.
 * We will probably not need any of them (cjk: chinese, japanese, korean characters, code: code blocks, math: math equations, mermaid: flowcharts, sequence diagrams, state diagrams and more using text based syntax.) so they are not included here.
 * If needed, they can be passed to the MessageResponse component as plugins prop.
 */

import 'streamdown/styles.css';

import { memo, type ComponentProps, type HTMLAttributes } from 'react';
import { Streamdown, type StreamdownProps } from 'streamdown';
import { cn } from '../../../lib/utils/cn';
import type { MessageRole } from './types';

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: MessageRole;
};

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    data-from={from}
    className={cn(
      'group flex w-full max-w-[95%] flex-col gap-2',
      'data-[from=user]:ml-auto data-[from=user]:justify-end',
      className,
    )}
    {...props}
  />
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement>;

export const MessageContent = ({ children, className, ...props }: MessageContentProps) => (
  <div
    className={cn(
      'flex w-fit max-w-full min-w-0 flex-col gap-2 overflow-hidden text-base',
      // role-specific styles via group data attribute set on Message
      'group-data-[from=user]:ml-auto group-data-[from=user]:rounded-lg group-data-[from=user]:bg-indigo-crush-10 group-data-[from=user]:px-4 group-data-[from=user]:py-3 group-data-[from=user]:text-space',
      // assistant styles
      'group-data-[from=assistant]:text-space',
      className,
    )}
    {...props}>
    {children}
  </div>
);

export type MessageActionsProps = ComponentProps<'div'>;

export const MessageActions = ({ className, children, ...props }: MessageActionsProps) => (
  <div className={cn('flex items-center gap-1', className)} {...props}>
    {children}
  </div>
);

export type MessageResponseProps = StreamdownProps;

export const MessageResponse = memo(({ className, plugins, ...props }: MessageResponseProps) => (
  <Streamdown
    className={cn(
      'size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
      // List markers hang outside the item, so an item whose content is a
      // paragraph keeps its text on the marker's line instead of starting below
      // it; the padding keeps the markers inside the message.
      '[&_ol]:list-outside [&_ol]:pl-6 [&_ul]:list-outside [&_ul]:pl-6',
      className,
    )}
    plugins={plugins}
    {...props}
  />
));

MessageResponse.displayName = 'MessageResponse';
